import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, Switch, Typography, useMediaQuery, useTheme } from '@mui/material';
import CustomDialog from '@ui/components/common/Dialog';
import { useEffect, useMemo } from 'react';
import BDrawer from '@ui/components/common/BDrawer';
import Grid from '@mui/material/Grid2';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import { Icon } from '@iconify/react';
import { Controller, useForm } from 'react-hook-form';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { useTranslation } from 'react-i18next';
import { HDatePicker, HTimePicker } from '@ui/components/common/Pickers';
import FormValidationMessage from '@ui/components/FormValidationMessage';
import { useAppDispatch } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { getAllInterestsWithoutPaginationAsync } from '@application/features/interests/interestsThunk';
import { BenefitTypeEnum } from '@application/enums/benefit-type-enum';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { createPartnerEventAsync, updatePartnerEventAsync } from '@application/features/partnerEvent/partnerEventThunk';
import { getTranslatedLanguageLabel } from '@application/helpers/language-display-helper';
import { filterLanguagesByEventType } from '@application/helpers/language-filter-helper';
import { hasPermission } from '@application/helpers/permission-helper';
import { getVDKEventByIdAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { EventDocumentType } from '@application/enums/event-document-type';
import { DEFAULT_BACKGROUND_IMAGE_URLS, assetToFile } from '@application/helpers/background-image-helper';
const NewEventDialogDrawer = ({ open, onClose, eventId, isEdit }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            eventTitle: '',
            location: '',
            externalsAllowed: null,
            isMultiDayEvent: false,
            eventDuration: { startDate: '', endDate: '' },
            startTime: null,
            endTime: null,
            interests: []
        }
    });
    // Helper function to map interests for multi-select
    const mapInterestsToSelect = (interests = []) => {
        return interests.map((interest) => ({
            label: interest.title,
            value: interest.id
        }));
    };
    const mapLanguagesToDropdown = (languagesDto = []) => {
        return languagesDto.map((lang) => ({
            label: getTranslatedLanguageLabel(t, lang),
            value: lang.id,
            iconUrl: lang.flagUrl
        }));
    };
    // Function to apply event data to form
    const applyEventToForm = (event) => {
        // Apply the event data to the form
        reset({
            eventTitle: event.title || '',
            location: event.location || '',
            externalsAllowed: event.isExternalAllowed ?? null,
            isMultiDayEvent: event.startDate !== event.endDate,
            eventDuration: { startDate: event.startDate, endDate: event.endDate },
            startTime: event.startTime || null,
            endTime: event.endTime || null,
            interests: mapInterestsToSelect(event.eventInterest),
            languageIds: mapLanguagesToDropdown(event.eventLanguages) || [],
        });
    };
    const dispatch = useAppDispatch();
    const { interestswithoutpagination } = useSelector((state) => state.interests);
    const { partner } = useSelector((state) => state.partner);
    const { id: partnerId } = useParams();
    const { VDKEvent } = useSelector((state) => state.VDKEvent);
    const { languages } = useSelector((state) => state.languages);
    // Permission checks
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canCreateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Create');
    const canUpdateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Update');
    const canUpdatePartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Update');
    useEffect(() => {
        dispatch(getAllInterestsWithoutPaginationAsync());
    }, [dispatch]);
    useEffect(() => {
        if (!open)
            return;
        if (!isEdit) {
            // Pre-select partner's interests/tags when creating a new event
            const partnerInterests = partner?.interestIds && partner?.interestTitles
                ? partner.interestIds
                    .map((id, index) => ({
                    value: id,
                    label: partner.interestTitles?.[index] || ''
                }))
                    .filter((interest) => interest.label !== '')
                : [];
            reset({
                eventTitle: '',
                location: '',
                externalsAllowed: null,
                isMultiDayEvent: false,
                eventDuration: { startDate: '', endDate: '' },
                startTime: null,
                endTime: null,
                interests: partnerInterests
            });
        }
        else if (eventId && isEdit) {
            dispatch(getVDKEventByIdAsync({
                id: eventId,
                documentType: EventDocumentType.BackgroundImage
            }))
                .unwrap()
                .then(applyEventToForm);
        }
    }, [dispatch, open, eventId, isEdit, partner]);
    const interestOptions = useMemo(() => (interestswithoutpagination || []).map((interest) => ({
        label: interest.title,
        value: interest.id
    })), [interestswithoutpagination]);
    const languageOptions = useMemo(() => {
        // Filter SV for non-FamilyFest events (partner events are not FamilyFest)
        const filteredLanguages = filterLanguagesByEventType(languages, false);
        return (filteredLanguages || []).map((lang) => ({
            label: getTranslatedLanguageLabel(t, lang),
            value: lang.id,
            iconUrl: lang.flagUrl
        }));
    }, [languages, t, i18n.language]);
    const onSubmit = async (data) => {
        // Permission check as safety measure
        if (isEdit && (!canUpdateEvents || !canUpdatePartners)) {
            toast.error(t('common.unauthorized') || 'You do not have permission to update events');
            return;
        }
        if (!isEdit && (!canCreateEvents || !canUpdatePartners)) {
            toast.error(t('common.unauthorized') || 'You do not have permission to create events');
            return;
        }
        try {
            // Convert default background image to File
            const defaultBackgroundImageUrl = DEFAULT_BACKGROUND_IMAGE_URLS[0];
            const backgroundImageFile = await assetToFile(defaultBackgroundImageUrl, 'defaultBg.jpg');
            const languageIds = data.languageIds ? data.languageIds.map((lang) => lang.value) : [];
            const request = {
                partnerId: partnerId,
                title: data.eventTitle,
                location: (data?.location ?? '').trim() || null,
                backgroundImages: [
                    {
                        Image: backgroundImageFile,
                        IsBackgroundImage: true
                    }
                ],
                startDate: data?.eventDuration?.startDate || '',
                endDate: data?.eventDuration?.endDate || '',
                startTime: data?.startTime || undefined,
                endTime: data?.endTime || undefined,
                dueRegistrationDate: data?.eventDuration?.endDate || '',
                isExternalAllowed: Boolean(data?.externalsAllowed),
                isPasswordProtection: false,
                password: undefined,
                eventCompanyIds: partner?.companyIds || [],
                collaboratorIds: [],
                interestIds: Array.isArray(data?.interests) ? data.interests.map((o) => o.value) : [],
                benefitType: BenefitTypeEnum.Tickets,
                languageIds: languageIds || [],
            };
            if (isEdit && eventId) {
                // Update Event Request
                const updateRequest = {
                    id: eventId,
                    title: data.eventTitle,
                    location: (data?.location ?? '').trim() || null,
                    backgroundImages: [
                        {
                            Image: backgroundImageFile,
                            IsBackgroundImage: true
                        }
                    ],
                    startDate: data?.eventDuration?.startDate || '',
                    endDate: data?.eventDuration?.endDate || '',
                    startTime: data?.startTime || undefined,
                    endTime: data?.endTime || undefined,
                    dueRegistrationDate: data?.eventDuration?.endDate || '',
                    isExternalAllowed: Boolean(data?.externalsAllowed),
                    isPasswordProtection: false,
                    password: undefined,
                    eventCompanyIds: partner?.companyIds || [],
                    collaboratorIds: [],
                    interestIds: Array.isArray(data?.interests)
                        ? data.interests.map((o) => o.value)
                        : [],
                    benefitType: BenefitTypeEnum.Tickets,
                    isSponsorPartnerEvent: VDKEvent?.isSponsorPartnerEvent,
                    isSponsorPartnerExternalEvent: VDKEvent?.isSponsorPartnerExternalEvent,
                    languageIds: languageIds || [],
                };
                // Dispatch Update Event Request
                dispatch(updatePartnerEventAsync(updateRequest))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || 'Event updated successfully!');
                    }
                    else {
                        toast.error(response.message || 'Event update failed');
                    }
                })
                    .catch(() => {
                    toast.error('Error occurred while updating the event.');
                });
            }
            else {
                // Create Event Request
                dispatch(createPartnerEventAsync(request))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || 'Event created successfully!');
                    }
                    else {
                        toast.error(response.message || 'Event created failed');
                    }
                });
            }
            reset({
                eventTitle: '',
                location: '',
                externalsAllowed: null,
                isMultiDayEvent: false,
                eventDuration: { startDate: '', endDate: '' },
                startTime: null,
                endTime: null,
                languageIds: [],
            });
            onClose();
        }
        catch (error) {
            toast.error('Error occurred while processing the event.');
        }
    };
    const externalsAllowedOptions = [
        { label: t('events.createNewEvent.yes'), value: true },
        { label: t('events.createNewEvent.no'), value: false }
    ];
    const actionButtons = (_jsxs(Box, { sx: { display: 'flex', gap: '12px', justifyContent: 'center', width: '100%' }, children: [_jsx(Button, { size: 'large', variant: 'outlined', color: 'inherit', fullWidth: true, onClick: onClose, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'inherit', fullWidth: true, disabled: isEdit ? !canUpdateEvents || !canUpdatePartners : !canCreateEvents || !canUpdatePartners, endIcon: _jsx(Icon, { icon: 'eva:checkmark-fill' }), onClick: handleSubmit(onSubmit), children: isEdit ? t('events.editEvent') : t('events.createEvent') })] }));
    const dialogContent = (_jsx(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            px: { xs: 0, md: 2 },
            pb: { xs: 0, md: 0 }
        }, children: _jsxs("form", { onSubmit: handleSubmit(onSubmit), children: [_jsxs(Grid, { container: true, spacing: 1.5, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', name: 'eventTitle', control: control, label: t('labels.eventTitle'), errorMessage: errors.eventTitle?.message, rules: {
                                    required: t('validations.required', {
                                        field: t('labels.eventTitle')
                                    }),
                                    minLength: {
                                        value: 3,
                                        message: t('validations.minLength', {
                                            field: t('labels.eventTitle'),
                                            min: 3
                                        })
                                    },
                                    maxLength: {
                                        value: 100,
                                        message: t('validations.maxLength', {
                                            field: t('labels.eventTitle'),
                                            max: 100
                                        })
                                    }
                                } }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', name: 'location', control: control, label: t('labels.location'), placeholder: t('labels.location') }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(SingleSelect, { name: 'externalsAllowed', label: t('labels.externalsAllowed'), options: externalsAllowedOptions, control: control, bg: 'grey8', error: errors.externalsAllowed?.message, rules: {
                                    validate: (value) => {
                                        if (value === null || value === undefined) {
                                            return t('validations.required', {
                                                field: t('labels.externalsAllowed')
                                            });
                                        }
                                        return true;
                                    }
                                } }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(Controller, { name: 'languageIds', control: control, rules: {
                                    required: t('validations.required', {
                                        field: t('labels.languages')
                                    }),
                                    validate: (value) => {
                                        if (!value || value.length === 0) {
                                            return t('validations.required', {
                                                field: t('labels.languages')
                                            });
                                        }
                                        return true;
                                    }
                                }, render: ({ field }) => {
                                    const primaryLanguageId = watch('primaryLanguageId');
                                    // Enhance language options with primary indicator
                                    const enhancedLanguageOptions = languageOptions.map((option) => {
                                        const isPrimaryValue = Boolean(primaryLanguageId && String(option.value) === String(primaryLanguageId));
                                        return {
                                            label: option.label,
                                            value: option.value,
                                            iconUrl: option.iconUrl,
                                            isDisabled: option.isDisabled,
                                            isPrimary: isPrimaryValue ? true : undefined,
                                            primaryBadge: isPrimaryValue ? true : undefined,
                                            primaryLabel: isPrimaryValue ? t('labels.primary') || 'Primary' : undefined
                                        };
                                    });
                                    return (_jsx(Box, { children: _jsx(ControlledHCheckboxAutocomplete, { name: 'languageIds', control: control, bg: 'grey8', options: enhancedLanguageOptions, label: t('labels.languages'), error: errors.languageIds?.message, getOptionKey: (option) => option.value, showSelectAll: true, isSortable: true, selectAllLabel: t('actions.selectAll') }) }));
                                } }) }), _jsxs(Grid, { size: { xs: 12 }, children: [_jsx(Controller, { name: 'isMultiDayEvent', control: control, render: ({ field }) => (_jsxs(Box, { display: 'flex', alignItems: 'center', children: [_jsx(Switch, { checked: field.value, onChange: (_, checked) => {
                                                    field.onChange(checked);
                                                    if (!checked) {
                                                        const start = watch('eventDuration.startDate') || '';
                                                        setValue('eventDuration.endDate', start);
                                                    }
                                                    else {
                                                        setValue('eventDuration.endDate', '');
                                                    }
                                                } }), _jsx(Typography, { variant: 'body2', children: t('labels.multipleDayEvent') })] })) }), _jsx(Controller, { name: 'eventDuration', control: control, rules: {
                                        validate: (value) => {
                                            if (!value?.startDate || value.startDate === '') {
                                                return t('events.dateRangeRequired');
                                            }
                                            if (watch('isMultiDayEvent') && (!value?.endDate || value.endDate === '')) {
                                                return t('events.dateRangeRequired');
                                            }
                                            return true;
                                        }
                                    }, render: ({ field, fieldState }) => {
                                        const isMultiDay = watch('isMultiDayEvent');
                                        return (_jsxs(Box, { children: [isMultiDay ? (_jsxs(Box, { display: 'flex', gap: 1.5, children: [_jsx(HDatePicker, { value: field.value?.startDate || '', handleSelectedDate: (date) => field.onChange({ ...field.value, startDate: date || '' }), label: t('labels.startDate') + '*', bg: 'grey8', readOnly: false, maxDate: field.value?.endDate ? new Date(field.value.endDate) : undefined }), _jsx(HDatePicker, { value: field.value?.endDate || '', handleSelectedDate: (date) => field.onChange({ ...field.value, endDate: date || '' }), label: t('labels.endDate') + '*', bg: 'grey8', readOnly: false, minDate: field.value?.startDate ? new Date(field.value.startDate) : undefined })] })) : (_jsx(HDatePicker, { value: field.value?.startDate || '', handleSelectedDate: (date) => {
                                                        const d = date || '';
                                                        field.onChange({ startDate: d, endDate: d });
                                                    }, label: t('labels.eventDay') + '*', bg: 'grey8', readOnly: false })), fieldState.error && (_jsx(FormValidationMessage, { error: fieldState.error.message || '' }))] }));
                                    } })] }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(Controller, { name: 'startTime', control: control, rules: {
                                    required: t('validations.required', { field: t('labels.startTime') }),
                                    validate: (value) => {
                                        if (!value || value === null || value === '') {
                                            return t('validations.required', { field: t('labels.startTime') });
                                        }
                                        return true;
                                    }
                                }, render: ({ field, fieldState }) => {
                                    return (_jsxs(Box, { children: [_jsx(HTimePicker, { value: field.value, handleSelectedTime: (time) => {
                                                    field.onChange(time);
                                                }, label: t('labels.startTime'), bg: 'grey8', readOnly: false }), fieldState.error && (_jsx(FormValidationMessage, { error: fieldState.error.message || '' }))] }));
                                } }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(Controller, { name: 'endTime', control: control, rules: {
                                    required: t('validations.required', { field: t('labels.endTime') }),
                                    validate: (value) => {
                                        if (!value || value === null || value === '') {
                                            return t('validations.required', { field: t('labels.endTime') });
                                        }
                                        return true;
                                    }
                                }, render: ({ field, fieldState }) => {
                                    return (_jsxs(Box, { children: [_jsx(HTimePicker, { value: field.value, handleSelectedTime: (time) => {
                                                    field.onChange(time);
                                                }, label: t('labels.endTime'), bg: 'grey8', readOnly: false }), fieldState.error && (_jsx(FormValidationMessage, { error: fieldState.error.message || '' }))] }));
                                } }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', name: 'interests', control: control, options: interestOptions, label: t('labels.tags'), error: errors.interests?.message, showSelectAll: true, isSortable: true, selectAllLabel: t('actions.selectAll'), rules: {} }) }), _jsx(Grid, { size: { xs: 12, sm: 6 } })] }), isMobile && _jsx(Box, { mt: 1, children: actionButtons })] }) }));
    return (_jsxs(_Fragment, { children: [!isMobile && (_jsx(CustomDialog, { backgroundVariant: 'white', open: open, onClose: onClose, titleWithIcon: isEdit ? t('events.editEvent') : t('events.newEvent'), titleIcon: '/assets/images/starIcon.png', maxWidth: 'md', actions: actionButtons, children: dialogContent })), isMobile && (_jsx(BDrawer, { title: isEdit ? t('events.editEvent') : t('events.newEvent'), anchor: 'bottom', open: open, onClose: onClose, showCloseButton: true, children: _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: dialogContent }) }))] }));
};
export default NewEventDialogDrawer;
